import {PipelineElement, piplineConfig} from "./PipelineConfig";
import styled from "styled-components";
import {RefObject, useEffect, useMemo, useRef, useState} from "react";
import {CustomSelect} from "../CustomSelect";
import {LogLine} from "../../api/types";
import AnimatedHeight from "../AnimatedHeight";
import Anchor from "../anchors/Anchor";

interface Props {
    current: PipelineElement;
    indentation?: number;
    setActive?: (element: PipelineElement) => void;
    active: PipelineElement | null;
    addItem?: (type: string, parent: PipelineElement) => void;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
    moveUp?: (element: PipelineElement) => void;
    moveDown?: (element: PipelineElement) => void;
    removeItem?: (element: PipelineElement) => void;
    onRun?: (element: PipelineElement) => void;
    isNotTest?: boolean;
    htmlElement?: HTMLElement | null;
    isRunning?: boolean;
    reparent?: PipelineElement | null;
    setReparent?: (element: PipelineElement | null) => void;
    moveFully?: (element: PipelineElement, dir: 'before' | 'after') => void;
    insideTask?: boolean;
}

const Container = styled.div`
    .item {
        padding: .5rem;
        width: calc(100% - 2rem);
        position: relative;

        &:hover {
            background-color: var(--accent-blue-hover);
        }
    }

    .selected {
        background-color: var(--accent-blue-selected);
        color: white;

        &:hover {
            background-color: var(--accent-blue-selected-hover);
        }
    }

    .reparent {
        background-color: var(--primary-blue-selected);
        color: white;

        &:hover {
            background-color: var(--primary-blue-selected-hover);
        }
    }

    .hidden {
        position: absolute;
        right: 0;
        visibility: hidden;
    }

    .open {
        visibility: visible;
    }

    .right-buttons {
        position: absolute;
        right: 0;
        top: .75rem;
        display: flex;
        gap: .5rem;
    }

    .arrow-container {
        width: 1.25rem;
        display: grid;
        position: relative;
        margin-right: .5rem;
    }

    .arrow-container2 {
        position: absolute;
        display: grid;
        top: -.75rem;
        right: .25rem;
    }

    .arrow {
        padding: .25rem;
        width: fit-content;
        height: 50%;
        font-size: 1rem;
        cursor: pointer;

        &:hover {
            background-color: var(--primary-blue-hover);
        }
    }

    button {
        background-color: var(--primary-blue-selected);
        border: none;
        color: white;
        padding: .25rem;
        border-radius: .25rem;
        cursor: pointer;
    }

    button:hover {
        background-color: var(--primary-blue-selected-hover);
    }

    .tree-status {
        border-radius: 0;
        margin: 0;
        position: absolute;
        display: inline-block;
        width: .5rem;
        height: 100%;
        left: 0;
        top: 0;
        bottom: 0;

        background-color: dodgerblue;
    }

    .tree-status-running {
        background-color: #ffcc00;
    }

    .tree-status-error {
        background-color: #ff0000;
    }

    .tree-status-finished {
        background-color: #00ff00;
    }

`;

const ContextMenu = styled.div`
    
    .context-item {
        padding: .5rem 1rem;
        color: white;
        font-size: 1rem;
        cursor: pointer;
        transition: background-color .1s ease-in-out;
    }
    
    .context-item:hover {
        background-color: var(--primary-blue-hover);
        cursor: pointer;
    }    
`;

const Logs = styled.div`

    transition: max-height .3s ease-in-out;
    height: 0;

`;

const LogsContainer = styled.div`

    .log-container {
        max-height: 30rem;
        overflow-y: auto;
    }

    p {
        margin: 0;
    }

    .log-level {


        &-info {
            color: dodgerblue;
        }

        &-error {
            color: #ff0000;
        }

        &-warning {
            color: #ffcc00;
        }

        &-debug {
            color: #00ff00;
        }
    }

`;

export default function PipelineTree({
                                         current,
                                         indentation,
                                         setActive,
                                         active,
                                         addItem,
                                         canMoveUp,
                                         canMoveDown,
                                         moveUp,
                                         moveDown,
                                         removeItem,
                                         onRun,
                                         isNotTest,
                                         htmlElement,
                                         isRunning,
                                         reparent,
                                         setReparent,
                                         moveFully,
                                         insideTask
                                     }: Props) {
    const [open, setOpen] = useState(false);
    const [openLog, setOpenLog] = useState<string[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [hasChildrenExtended, setHasChildrenExtended] = useState(true);


    const openBox = () => {
        setOpen(true);
    }

    const config = useMemo(() =>
            piplineConfig.find(value => value.type === current.type)
        , [current.type]);

    // @ts-ignore
    const children = useMemo(() => config && config.getChildren ? config.getChildren(current) : [], [config, current]);

    const left = ((indentation ?? 0) * .75);

    const logExtended = useMemo(() => {
        return !!((current.lastStatus === 'running' || current.lastStatus === 'error' || (active && current.id === active.id) || openLog.includes(current.id)) && (current.logs && current.logs.length));
    }, [current, current.lastStatus, current.id, current.logs, current.logs?.length, openLog, active, active?.id]);

    function getRelativeOffset(child: HTMLElement, parent: HTMLElement): number {
        let offset = 0;
        let el: HTMLElement | null = child;
        while (el && el !== parent) {
            offset += el.offsetTop;
            el = el.offsetParent as HTMLElement;
        }
        return offset;
    }

    const [wasOpen, setWasOpen] = useState(false);

    useEffect(() => {
        if (logExtended && htmlElement && containerRef.current && !wasOpen && isRunning) {
            const offset = containerRef.current.offsetTop - htmlElement.offsetTop;
            const center = offset - (htmlElement.clientHeight / 2) + (containerRef.current.clientHeight / 2);

            htmlElement.scrollTo({
                top: center,
                behavior: 'smooth'
            });
        }
        setWasOpen(logExtended);
    }, [current.id, logExtended, htmlElement, isRunning, wasOpen]);

    const anchorRef = useRef<HTMLDivElement>(null);

    const [menu, setMenu] = useState<{ x: number, y: number } | null>(null);

    const handleContextMenu = (e: React.MouseEvent) => {
        if (isNotTest) {
            return;
        }
        e.preventDefault();
        setTimeout(() => {
            setMenu({x: e.clientX, y: e.clientY});
        }, 10);
    };

    useEffect(() => {
        if (!menu) return;
        const close = (e: MouseEvent) => {
            if (!menu) return;
            /*const menuEl = document.getElementById("custom-context-menu");
            if (menuEl && e.target && menuEl.contains(e.target as Node)) return;*/
            setMenu(null);
        };
        window.addEventListener("click", close);
        window.addEventListener("contextmenu", close);
        return () => {
            window.removeEventListener("click", close);
            window.removeEventListener("contextmenu", close);
        };
    }, [menu]);

    return (
        <>
            <div style={{width: '100%', height: '0', position: 'relative'}}
                 className={'hidden' + (open ? ' open' : '')}>
                <div
                    style={{position: 'absolute', width: 'fit-content', height: 'fit-content', right: '0', top: '2rem'}}
                    ref={anchorRef}>
                    <Anchor open={open} anchorRef={anchorRef}>
                        <CustomSelect
                            options={config?.possibleNext ?? []}
                            onSelect={type => {
                                if (addItem)
                                    addItem(type, current);
                            }}
                            open={open}
                            setOpen={setOpen}
                        />
                    </Anchor>
                </div>
            </div>
            <Container ref={containerRef} onContextMenu={handleContextMenu} onClick={() => {
                if (setActive)
                    setActive(current);
                if (isNotTest) {
                    if (openLog.includes(current.id)) {
                        setOpenLog(openLog.filter(id => id !== current.id));
                    } else {
                        setOpenLog([...openLog, current.id]);
                    }
                }

            }}>
                <div style={{
                    borderLeft: left + 'rem solid var(--content-box-border)',
                    width: 'calc(100% - 2rem - ' + left + 'rem)'
                }}
                     className={'item' + ((active && active.id === current.id) ? ' selected' : '') + ((reparent && reparent.id === current.id) ? ' reparent' : '')}>
                    <div className={'tree-status tree-status-' + current.lastStatus}></div>
                    <div style={{
                        display: children.length ? 'inline-block' : 'none',
                        padding: '.5rem',
                        cursor: 'pointer'
                    }} onClick={(event) => {
                        setHasChildrenExtended(!hasChildrenExtended);
                        event.stopPropagation();
                        event.preventDefault();
                    }}>
                        {!hasChildrenExtended && (<i className="fa-solid fa-circle-plus"></i>)}
                        {hasChildrenExtended && (<i className="fa-solid fa-circle-minus"></i>)}
                    </div>
                    <span
                        style={{
                            marginLeft: children.length ? '0' : '.5rem',
                            padding: '.5rem',
                            display: 'inline-block'
                        }}>{config && config.preview ? config.preview(current) : current.type}</span>
                    {!isNotTest && !insideTask && (
                        <div className={'right-buttons'}>
                            <button style={{color: 'green', backgroundColor: 'transparent', border: 'white solid 1px'}}
                                    onClick={(event) => {
                                        if (onRun)
                                            onRun(current);
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}><i
                                className="fa-solid fa-play"></i> <span style={{color: 'white'}}>Run</span></button>
                            {config?.possibleNext && config.possibleNext.length > 0 && (
                                <button onClick={(event) => {
                                    openBox();
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}><i className="fa-solid fa-plus"></i></button>)}
                            <button
                                style={{visibility: !config?.canBeRemoved || config.canBeRemoved(current) ? 'visible' : 'hidden'}}
                                onClick={(event) => {
                                    if (removeItem)
                                        removeItem(current);
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}><i className="fa-solid fa-trash"></i></button>
                            <div className={'arrow-container'}>
                                <div className={'arrow-container2'}>
                                    <div className={'arrow'} style={{visibility: canMoveUp ? 'visible' : 'hidden'}}
                                         onClick={(event) => {
                                             event.preventDefault();
                                             event.stopPropagation();
                                             if (canMoveUp) {
                                                 if (moveUp)
                                                     moveUp(current);
                                             }
                                         }}><i
                                        className="fa-solid fa-arrow-up"></i></div>
                                    <div className={'arrow'} style={{visibility: canMoveDown ? 'visible' : 'hidden'}}
                                         onClick={(event) => {
                                             event.preventDefault();
                                             event.stopPropagation();
                                             if (canMoveDown) {
                                                 if (moveDown)
                                                     moveDown(current);
                                             }
                                         }}><i
                                        className="fa-solid fa-arrow-down"></i></div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

            </Container>
            {menu && (
                <ContextMenu
                    style={{
                        position: "fixed",
                        top: menu.y,
                        left: menu.x,
                        background: "#222",
                        color: "#fff",
                        borderRadius: 4,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        zIndex: 9999,
                        minWidth: 120,
                    }}
                >
                    <div className={'context-item'} onClick={() => {
                        if (setReparent) {
                            setReparent(current);
                        }
                        setMenu(null);
                    }}>Reparent
                    </div>
                    {active && (
                        <>
                            <div className={'context-item'}
                                 onClick={() => {
                                     if (moveFully) {
                                         moveFully(current, 'before');
                                     }
                                     setMenu(null);
                                 }}>Move Selected Before
                            </div>
                            <div className={'context-item'}
                                 onClick={() => {
                                     if (moveFully) {
                                         moveFully(current, 'after');
                                     }
                                     setMenu(null);
                                 }}>Move Selected After
                            </div>
                        </>
                    )}
                </ContextMenu>
            )}
            <Logs style={{maxHeight: logExtended ? '10rem' : '0', height: 'fit-content', overflowY: 'auto'}}>
                <LogsElement logs={current.logs}></LogsElement>
            </Logs>
            <AnimatedHeight open={hasChildrenExtended}>
                {children && children.map((item, index) => {

                    const config = piplineConfig.find(value => value.type === current.type);
                    if (!config) {
                        console.error('No config found for type:', current.type);
                        return null;
                    }

                    return (
                        <PipelineTree indentation={(indentation ?? 0) + 1} current={item} active={active}
                                      setActive={setActive}
                                      key={item.id} addItem={addItem}
                                      canMoveDown={children.length - 1 > index && !!config.moveChildren}
                                      canMoveUp={index > 0 && !!config.moveChildren} moveUp={moveUp} moveDown={moveDown}
                                      removeItem={removeItem} onRun={onRun} isNotTest={isNotTest}
                                      htmlElement={htmlElement}
                                      isRunning={isRunning} reparent={reparent}
                                      setReparent={setReparent} moveFully={moveFully}
                                      insideTask={insideTask || current.type === 'task'}></PipelineTree>)
                })}
            </AnimatedHeight>
        </>
    )
}

function LogsElement({logs}: { logs: LogLine[] | undefined }) {

    const logContainerRef = useRef<HTMLPreElement>(null);
    useEffect(() => {
        if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [logs, logs?.length, logContainerRef, logContainerRef.current]);

    return (
        <LogsContainer>
        <pre style={{
            textWrap: 'wrap',
            minHeight: '1rem',
            maxHeight: '9rem',
            margin: '0.25rem .5rem 0.25rem 0.25rem',
            marginRight: '1rem',
            backgroundColor: '#111115',
            padding: '.5rem'
        }} className={'log-container'} ref={logContainerRef}>
            {
                logs && logs.map((log1, index) => (
                    <p key={index} className={'log-line'}>
                        <span className={'log-level log-level-' + log1.level.toLowerCase()}>
                            {`[${log1.level}]: `}
                        </span>
                        <span>
                            {log1.message}
                        </span>
                    </p>
                ))
            }
        </pre>
        </LogsContainer>
    );
}